import {
  Billboard,
  Button,
  CloseButton,
  FileDrop,
  Flex,
  Heading,
  IconImageLine,
  Modal,
  Spinner,
  Text,
  TextInput,
} from "@instructure/ui";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { AttainmentRuleService } from "../../services/AttainmentRuleService";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";

const AddBadgeModal = ({
  openBadgeModal,
  setOpenBadgeModal,
  issuer,
  onBadgeCreated,
}) => {
  const { t } = useTranslation();
  const showNotification = useNotification();
  const [badgeName, setBadgeName] = useState("");
  const [badgeDesc, setBadgeDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [invalidFile, setInvalidFile] = useState(false);
  const [badgeImage, setBadgeImage] = useState(null);
  const [base64Image, setBase64Image] = useState("");

  const handleImageInputChange = (file) => {
    setInvalidFile(false);
    setBadgeImage(file);
    const reader = new FileReader();

    reader.onload = (e) => {
      const base64String = e.target.result;
      setBase64Image(base64String);
    };

    reader.readAsDataURL(file);
  };

  const handleSubmit = () => {
    setLoading(true);
    AttainmentRuleService.createBadgeClassByIssuer(issuer.entityId, {
      name: badgeName,
      description: badgeDesc,
      image: base64Image,
      criteriaNarrative: badgeDesc,
    })
      .then((badge) => {
        onBadgeCreated(badge);
        setLoading(false);
        setOpenBadgeModal(false);
      })
      .catch(() => {
        showNotification(
          t("notifications.general.createError", {
            obj: t("badging.badgeClass"),
          }),
          ERROR
        );
      });
  };

  const handleClose = () => {
    setInvalidFile(false);
    setBadgeImage(null);
    setBadgeName("");
    setBadgeDesc("");
  };

  return (
    <Modal
      open={openBadgeModal}
      size="medium"
      label={t("badging.addNewBadge")}
      onExited={handleClose}
    >
      <Modal.Header>
        <Flex justifyItems="space-between">
          <Flex.Item>
            <Heading level="h2" as="h1">
              {`${t("badging.addNewBadgeFor")} ${issuer?.name}`}
            </Heading>
          </Flex.Item>
          <Flex.Item>
            <CloseButton
              screenReaderLabel={t("global.close")}
              onClick={() => setOpenBadgeModal(false)}
            />
          </Flex.Item>
        </Flex>
      </Modal.Header>
      <Modal.Body>
        <Flex wrap="wrap">
          <Flex.Item width="100%">
            <TextInput
              renderLabel={<Text size="small">{t("global.name")}</Text>}
              size="small"
              value={badgeName}
              onChange={(e) => setBadgeName(e.target.value)}
            />
          </Flex.Item>
          <Flex.Item width="100%" padding="medium 0 medium 0">
            <TextInput
              renderLabel={<Text size="small">{t("global.description")}</Text>}
              size="small"
              value={badgeDesc}
              onChange={(e) => setBadgeDesc(e.target.value)}
            />
          </Flex.Item>
          <Flex.Item width="100%" padding="0 0 small 0">
            <Text weight="bold" size="small">
              {t("badging.uploadBadgeImage")}
            </Text>
          </Flex.Item>
          <Flex.Item width="50%">
            <FileDrop
              shouldEnablePreview
              accept={[".png", ".svg"]}
              onDropAccepted={([file]) => {
                handleImageInputChange(file);
              }}
              onDropRejected={() => {
                setInvalidFile(true);
              }}
              messages={
                invalidFile
                  ? [
                      {
                        text: t("notifications.invalidFileType"),
                        type: "error",
                      },
                    ]
                  : []
              }
              renderLabel={
                <Billboard
                  size="small"
                  message={
                    <Flex justifyItems="center" wrap="wrap">
                      <Flex.Item width="100%">
                        <Text>{t("badging.availableFiles")}</Text>
                      </Flex.Item>
                      {badgeImage && (
                        <Flex.Item>
                          <Text weight="bold">{badgeImage.name}</Text>
                        </Flex.Item>
                      )}
                    </Flex>
                  }
                  hero={<IconImageLine />}
                />
              }
            />
          </Flex.Item>
          <Flex.Item textAlign="end" shouldGrow>
            {badgeImage && (
              <img
                src={badgeImage.preview}
                alt={t("badging.badgePreview")}
                width={150}
                height={150}
              />
            )}
          </Flex.Item>
        </Flex>
      </Modal.Body>
      <Modal.Footer>
        <Button margin="0 small 0 0" onClick={() => setOpenBadgeModal(false)}>
          {t("global.cancel")}
        </Button>

        <Button
          color="primary"
          type="submit"
          onClick={handleSubmit}
          interaction={
            badgeName && badgeDesc && badgeImage && !loading
              ? "enabled"
              : "disabled"
          }
        >
          {loading ? (
            <Spinner renderTitle={t("global.pleaseWait")} size="x-small" />
          ) : (
            t("global.submit")
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddBadgeModal;
